type Props = {
  size?: number;
  glow?: boolean;
  motion?: boolean;
  className?: string;
};

/**
 * Bouchon — Boulium signature 3D bouchon (target ball).
 * Acid-glowing inner core wrapped in a chrome rim. Used as hero/logo/loading.
 *
 * Server Component. Defs use hardcoded ids — a page only ever renders one
 * Bouchon (landing hero, login bouchon, or TopBar Logo). useId() was tried
 * but caused SSR/CSR hydration mismatches under Suspense in Next 16.
 *
 * From Claude Design handoff 2026-05-19.
 */
export function Bouchon({ size = 120, glow = true, motion = false, className }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      className={className}
      style={{
        display: "block",
        filter: glow ? `drop-shadow(0 0 ${size * 0.18}px var(--color-acid-glow))` : "none",
      }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="bouchon-chrome" cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="22%" stopColor="#e8e8e8" />
          <stop offset="55%" stopColor="#8a8a8a" />
          <stop offset="85%" stopColor="#2a2a2a" />
          <stop offset="100%" stopColor="#0e0e0e" />
        </radialGradient>
        <radialGradient id="bouchon-acid" cx="40%" cy="38%" r="65%">
          <stop offset="0%" stopColor="#f0ffae" />
          <stop offset="40%" stopColor="#c6ff3d" />
          <stop offset="80%" stopColor="#6b8c00" />
          <stop offset="100%" stopColor="#2a3a00" />
        </radialGradient>
        <radialGradient id="bouchon-hot" cx="38%" cy="32%" r="22%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        {motion && (
          <linearGradient id="bouchon-blur" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#c6ff3d" stopOpacity="0" />
            <stop offset="50%" stopColor="#c6ff3d" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#c6ff3d" stopOpacity="0" />
          </linearGradient>
        )}
      </defs>

      {motion && (
        <ellipse cx="100" cy="115" rx="90" ry="6" fill="url(#bouchon-blur)" opacity="0.7" />
      )}

      <ellipse cx="100" cy="180" rx="55" ry="6" fill="#000" opacity="0.55" />

      <circle cx="100" cy="100" r="82" fill="url(#bouchon-chrome)" />
      <circle cx="100" cy="100" r="62" fill="url(#bouchon-acid)" />
      <circle
        cx="100"
        cy="100"
        r="62"
        fill="none"
        stroke="#000"
        strokeOpacity="0.45"
        strokeWidth="1.5"
      />
      <ellipse
        cx="78"
        cy="78"
        rx="22"
        ry="14"
        fill="url(#bouchon-hot)"
        transform="rotate(-25 78 78)"
      />
      <path
        d="M 50 132 Q 100 152 150 132"
        stroke="#c6ff3d"
        strokeOpacity="0.35"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}
